import { useEffect, useState } from "react";
import api from "../../lib/api";
import { Package, FolderKanban, ClipboardList, BookOpen, BedDouble, TrendingUp, FileText } from "lucide-react";
import { LineChart, Line, PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";

const COLORS = ["#d6b06b", "#39c8c1", "#123b52", "#c49d58", "#2ba8a2", "#08111d"];

export default function AdminDashboard() {
  const [stats, setStats] = useState({});
  const [analytics, setAnalytics] = useState({ by_day: [], by_source: [], by_status: [], total_recent: 0 });

  useEffect(() => {
    api.get("/admin/stats").then((r) => setStats(r.data));
    api.get("/admin/analytics").then((r) => setAnalytics(r.data));
  }, []);

  const cards = [
    { label: "Toplam Ürün", value: stats.products || 0, icon: Package, color: "text-teal" },
    { label: "Kategoriler", value: stats.categories || 0, icon: FolderKanban, color: "text-gold" },
    { label: "Toplam Teklif", value: stats.quotes_total || 0, icon: ClipboardList, color: "text-teal" },
    { label: "Yeni Teklifler", value: stats.quotes_new || 0, icon: TrendingUp, color: "text-rose-500" },
    { label: "Blog Yazıları", value: stats.blog_posts || 0, icon: FileText, color: "text-gold" },
    { label: "Rehber Adımı", value: stats.opening_guide_steps || 0, icon: BookOpen, color: "text-gold" },
    { label: "Oda Tipleri", value: stats.room_types || 0, icon: BedDouble, color: "text-teal" },
    { label: "Son 14 gün teklif", value: analytics.total_recent || 0, icon: TrendingUp, color: "text-teal" },
  ];

  return (
    <div className="p-8 max-w-7xl" data-testid="admin-dashboard">
      <h1 className="heading-serif text-3xl font-semibold text-navy">Dashboard</h1>
      <p className="text-sm text-gray-500 mt-1">Otel Pro yönetim paneline hoş geldiniz.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-7">
        {cards.map((c) => (
          <div key={c.label} className="bg-white rounded-xl p-5 border border-gray-100 shadow-sm" data-testid={`stat-card-${c.label}`}>
            <div className="flex items-center gap-3">
              <div className={`h-10 w-10 rounded-md bg-softbg flex items-center justify-center ${c.color}`}>
                <c.icon size={18} />
              </div>
              <div>
                <div className="text-[10px] text-gray-500 uppercase tracking-wider">{c.label}</div>
                <div className="heading-serif text-2xl font-bold text-navy">{c.value}</div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid md:grid-cols-2 gap-5 mt-7">
        <div className="bg-white rounded-xl p-6 border border-gray-100">
          <h3 className="heading-serif font-semibold text-navy mb-4">Son 14 Gün — Teklif Trendi</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={analytics.by_day}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f5" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={(d) => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="count" stroke="#d6b06b" strokeWidth={2.5} dot={{ fill: "#d6b06b", r: 4 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-white rounded-xl p-6 border border-gray-100">
          <h3 className="heading-serif font-semibold text-navy mb-4">Kaynak Dağılımı</h3>
          <div className="h-64">
            {analytics.by_source.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={analytics.by_source} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label={{ fontSize: 10 }}>
                    {analytics.by_source.map((_, i) => (
                      <Cell key={i} fill={COLORS[i % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-400 text-sm">Henüz teklif yok.</div>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl p-6 border border-gray-100 mt-5">
        <h3 className="heading-serif font-semibold text-navy mb-4">Teklif Durumları</h3>
        <div className="h-56">
          {analytics.by_status.length > 0 ? (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={analytics.by_status}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f2f5" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="value" fill="#39c8c1" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-full flex items-center justify-center text-gray-400 text-sm">Henüz veri yok.</div>
          )}
        </div>
      </div>
    </div>
  );
}
